// tslint:disable:max-line-length
import * as lodash from "lodash";
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { DatePipe, Location } from "@angular/common";
import { NgForm } from "@angular/forms";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { SelectItem } from "primeng/primeng";
import { Observable, Subject, Subscription } from "rxjs";
import { Operation, Parameter, Path, Reference, Spec } from "swagger-schema-official";

import {
    BASE_CONSTANTS,
    BASE_URLS,
    GetServicesParameters,
    ITCIApplication,
    ITCIApplicationDeployRequest,
    TCIApplicationDeploymentMode,
    ITCIApplicationEndpoint,
    ITCIEnvVars,
    MasheryCredential,
    MasheryDomain,
    MasheryEndpoint,
    MasheryEndpointToPublish,
    MasheryPackage,
    MasheryPlan,
    MasheryService,
    TCI_ENVIRONMENT_VAR_TYPE,
    TCI_UPDATE_ACTION_TYPE,
    TCITenantAppType,
    TCITenantSelectorType,
    USER_ROLES,
    TCI_TENANT_SERVICE_PROVIDER_TYPE,
    SwaggerSpec,
    ITCIEndpointVisibilityType
} from "@tci/common";
import { NgWindowRefService } from "../../../ng-shared/ng-services/ng-window-ref.service";
import { NgToastrService } from "../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgToolsService } from "../../../ng-shared/ng-util/ng-tools.service";
import {ITCIAppRefreshNotification, NgAppChangeNotificationService} from "../../../ng-shared/ng-services/ng-app-change-notification.service";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details-menu";
import { AbstractTCITenantComponent } from "../../../ng-tenant/tci-tenant-component";
import { BaseAppDetailEndpointsService } from "./base-app-detail-endpoints.service";

declare var SwaggerUIBundle;
declare var SwaggerUIStandalonePreset;

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_ENDPOINTS.toString(),
    templateUrl: "./base-app-detail-endpoints.component.html",
    styleUrls: ["./base-app-detail-endpoints.component.less"]
})
export class BaseAppDetailEndpointsComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit, OnDestroy, AfterViewInit {

    app: ITCIApplication;
    withActions = true;
    endpoints: ITCIApplicationEndpoint[] = [];
    private originalEndpoints: ITCIApplicationEndpoint[] = [];
    selectedEndpoint: ITCIApplicationEndpoint;
    selectedEndpointId = "";
    isSwaggerUiShown = false;
    publishToMasheryDialogOpen = false;
    showHamburgerMenu: { [id: string]: boolean } = {};
    private endpointsInitialized$: Subject<boolean> = new Subject<boolean>();
    private subscriptions: { [key: string]: Subscription } = {};
    requestEndpoints = false;

    /* Start Mashery related properties */
    @ViewChild("publishToMasheryForm") private publishToMasheryForm: NgForm;
    @ViewChild("newAPIDefForm") private newAPIDefForm: NgForm;
    @ViewChild("newPackageForm") private newPackageForm: NgForm;
    @ViewChild("newPlanForm") private newPlanForm: NgForm;
    isAzureDeployment = false;
    private createApiDefinitionDialogOpen = false;
    private createPackageDialogOpen = false;
    private createPlanDialogOpen = false;
    private domains: MasheryDomain[] = [];
    private domainsToSelect: SelectItem[] = [];
    private selectedDomains: { domain: MasheryDomain } = {
        domain: null
    };
    private isLoggedIntoMashery = false;
    private isLoadingMasheryDialogContent = false;
    private masheryStep = -1;
    private masheryPlans: MasheryPlan[] = [];
    private masheryPackages: MasheryPackage[] = [];
    private masheryPackagesToSelect: SelectItem[] = [];
    private masheryServices: MasheryService[] = [];
    private masheryEndpoints: MasheryEndpoint[] = [];
    private reformattedMasheryServices: MasheryService[] = [];
    private orderedMasheryEndpoints: MasheryEndpoint[] = [];
    private selectedMasheryEndpoints: MasheryEndpoint[] = [];
    private masheryAction = "";
    private masheryControlCenter = "";
    private packageUrl = "";
    private addToPlanUrl = "";
    private apiDefinitionUrl = "";
    private masheryControlCenterUrl = "";
    private servicesFilterKeyword = "";
    private newServiceName = "";
    private newPackageName = "";
    private newPlanName = "";
    private isNewService = false;
    private isSavingPopover = false;
    private serviceRepeated = {};
    private allowSelfProvKeys = false;
    private masheryPublish: {
        auth: MasheryCredential;
        service: MasheryService;
        endpoints: MasheryEndpointToPublish[];
    };
    private selectedMasheryPackage: MasheryPackage = null;
    private selectedMasheryPlans: MasheryPlan[] = [];
    private sort = "";
    private TCIDomainName = "";

    // Pagination stuff
    private currentPage = 1;
    private itemsPerPage = 100;
    private lastSearch = "";
    private filterAttribute = "name";
    private loadingServices = false;

    isRORoleUser = false;
    private isAppBusy = false;
    canUpdateEndpoint = false;

    private filterBys = [
        {
            label: this.translate.instant("NG_APP_ENDPOINTS.PUBLISH_TO_MASHERY.NAME"),
            value: "name"
        },
        {
            label: this.translate.instant("NG_APP_ENDPOINTS.PUBLISH_TO_MASHERY.ID"),
            value: "id"
        },
        {
            label: this.translate.instant("NG_APP_ENDPOINTS.PUBLISH_TO_MASHERY.UPDATED"),
            value: "updated"
        },
        {
            label: this.translate.instant("NG_APP_ENDPOINTS.PUBLISH_TO_MASHERY.CREATED"),
            value: "created"
        }
    ];
    private filterBy = this.filterBys[0].value;

    /* End Mashery related properties */

    constructor(
        private datePipe: DatePipe,
        private endpointsService: BaseAppDetailEndpointsService,
        private appRefreshService: NgAppChangeNotificationService,
        private location: Location,
        private router: Router,
        private toastr: NgToastrService,
        private tools: NgToolsService,
        private translate: TranslateService,
        private windowRef: NgWindowRefService
    ) {
        super();
    }

    ngOnInit(): void {
        const urlFragmentInfo = this.parseUrlFragment(this.location.path(true));
        this.app = this.input.application;
        this.withActions = this.getTenantAppType() !== TCITenantAppType.scribe;
        this.selectedEndpointId = urlFragmentInfo.endpointId;
        this.getUserAuthForEndpoints();
        this.checkUserCanUpdateEndpoint();
        this.getAllAppEndpoints();
        this.monitorURLFragmentChange();
        this.initializeMasheryPublish();
        // this.returnAndRefreshEndpointList();
        this.initEventsWatch();
        this
            .endpointsService
            .getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.AZURE_CONNECTION, TCI_ENVIRONMENT_VAR_TYPE.PUBLIC_TCI_DOMAIN_NAME)
            .subscribe(
                (envVars: ITCIEnvVars<string>) => {
                    this.isAzureDeployment = envVars[TCI_ENVIRONMENT_VAR_TYPE.AZURE_CONNECTION] === "true";
                    this.TCIDomainName = envVars[TCI_ENVIRONMENT_VAR_TYPE.PUBLIC_TCI_DOMAIN_NAME];
                }
            );
        this
            .endpointsService
            .getControlCenterUrl()
            .subscribe(
                (controlCenterUrl: string) => {
                    this.masheryControlCenterUrl = controlCenterUrl;
                }
            );
    }

    ngOnDestroy(): void {
        lodash.forEach(
            lodash.values(this.subscriptions),
            (subscription: Subscription) => subscription && subscription.unsubscribe()
        );
    }

    ngAfterViewInit(): void {
        this
            .endpointsInitialized$
            .subscribe(
                (initialized: boolean) => {
                    if (initialized && this.selectedEndpointId) {
                        this.renderSwaggerUI();
                    }
                }
            );
    }

    private initEventsWatch() {
        const watchedSelectors = [
            TCITenantSelectorType.TCI_APP_DETAIL_REPLACE_APPLICATION,
            TCITenantSelectorType.TCI_APP_DETAIL_SET_ENDPOINT
        ].map(s => s.toString());

        this.subscriptions.getRefreshSubject = this.appRefreshService.getRefreshSubject() // IPAS-15787 - Refresh after app replace
            .filter(data =>
                // Note: The last conditional judgment value MUST be boolean
                data.source &&
                data.app && data.app.id === this.app.id
            )
            .subscribe((res: ITCIAppRefreshNotification) => {
            /**
             * IPAS-15787 - rzavala 2 dec 2020
             * For app-replace the refresh would happen twice: on HM output and after
             * the deployment has finished. This is in order to have the tab data
             * displayed immediately after replace action.
             *
             * IPAS-13049
             * For case 1, 2: scale up/down app
             * For case 3: set endpoint visibility (Public / Tibco Cloud Mesh)
             * For case 4: replace app, will scale app
             * need to refresh and return to endpoint list page
             */
            if (res.source && res.source.selector && watchedSelectors.includes(res.source.selector.toString())) {
                // Node: for app replace and set endpoint visibility, will be back to endpoint list page from swagger view
                this.backToEndpointsList();
                this.getAllAppEndpoints();
            } else if (res.source && res.source.provider && res.source.provider === TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DEPLOY
                        && this.selectedEndpoint && this.selectedEndpointId) {
                // Node: rerender Swagger UI when app deployed
                this.renderSwaggerUI();
            }
        });
    }

    editEndpoint(endpoint: ITCIApplicationEndpoint, event: any, isDescription = false) {
        let isShowingEndpointError = false;
        this.endpoints.forEach(item => {
            if (item.titleError || item.descriptionError) {
                isShowingEndpointError = true;
                return false;
            }
        });
        if (!isShowingEndpointError) {
            this.resetEditEndpointStatus();
            if (isDescription) {
                endpoint.isDescriptionEditing = true;
            } else {
                endpoint.isTitleEditing = true;
            }
            if (event && event.target) {
                this.tools.setTimeout(() => {
                    event.target.parentElement.firstChild.focus();
                });
            }
        }
    }

    keyupUpdateEndpointDetail(endpoint: ITCIApplicationEndpoint, event: any, isDescription = false): void {
        event.preventDefault();
        if (event.key === "Escape") {
            this.revertEndpoints();
        } else if (event.key === "Enter" && !isDescription) {
            this.updateEndpointDetail(endpoint, event, isDescription);
        }
    }
    updateEndpointDetail(endpoint: ITCIApplicationEndpoint, event: any, isDescription = false): void {
        if (this.isEndpointDirty(endpoint, isDescription)) {
            if (this.subscriptions.saveEndpointSub) {
                this.subscriptions.saveEndpointSub.unsubscribe();
            }
            this.subscriptions.saveEndpointSub = this.endpointsService.saveEndpoint(this.app.id, this.app.sandboxId, endpoint, isDescription)
                .subscribe(
                    // tslint:disable-next-line:no-shadowed-variable
                    (newEndpoint: ITCIApplicationEndpoint) => {
                        this.resetEditEndpointStatus();
                        this.initEndpoints(this.endpoints);
                    },
                    error => {
                        // Note: 1. Clean all editing error, only show error for current name or description
                        //       2. Revert all to original value, except current name or description
                        const msg = error ? error.json().msg : "Update endpoint failed.";
                        this.endpoints.forEach(item => {
                            if (item.id === endpoint.id) {
                                if (isDescription) {
                                    item.isDescriptionEditing = true;
                                    item.descriptionError = msg;
                                } else {
                                    item.isTitleEditing = true;
                                    item.titleError = msg;
                                }
                            }
                        });
                        if (event && event.target) {
                            this.tools.setTimeout(() => {
                                event.target.parentElement.firstChild.focus();
                            });
                        }
                    }
                );
        } else {
            this.resetEditEndpointStatus();
        }
    }

    private resetEditEndpointStatus() {
        this.endpoints.forEach(item => {
            item.titleError = "";
            item.descriptionError = "";
            item.isTitleEditing = false;
            item.isDescriptionEditing = false;
        });
    }

    private isEndpointDirty(endpoint: ITCIApplicationEndpoint, isDescription: boolean): boolean {
        let isDirty = false;
        this.originalEndpoints.forEach(item => {
            if (item.id === endpoint.id) {
                if (isDescription) {
                    isDirty = item.description !== endpoint.description.trim();
                } else {
                    isDirty = item.title !== endpoint.title.trim();
                }
            }
        });
        return isDirty;
    }

    private restoreEndpoint() {
        this.initEndpoints(this.originalEndpoints);
    }

    private initEndpoints(endpoints: ITCIApplicationEndpoint[]) {
        // Note: sort endpoints data, make swagger endpoint always on top of the table.
        endpoints = lodash.orderBy(endpoints, ["hasSwagger", "id"], ["desc"]);
        /**
         * Update endpointUrl to extercom URL for private endpoint copy
         */
        if (this.app.endpointVisibility === ITCIEndpointVisibilityType.private) {
            if (endpoints && Array.isArray(endpoints) && endpoints.length) {
                endpoints.forEach(endpoint => {
                    if (endpoint.hasSwagger) {
                        const swaggerJson: SwaggerSpec = endpoint.swaggerJson;

                        if (swaggerJson && "swagger" in swaggerJson) {
                            /**
                             * Swagger 2.x
                             * host ex: localdev-integration.cloud.tibcoapps.com:97
                             * basePath ex: /gsbc/01F3ZXQAEQEH5J5WF3GZ4X1195/tci/7cehi6bp6ziesj2woyzjcgby2j25wqtp
                             * Add prefix "/extercom" to the basePath
                             */
                            const extercomURL = `https://${swaggerJson.host}${BASE_CONSTANTS.EXTERCOM}${swaggerJson.basePath}`;
                            endpoint.endpointUrl = extercomURL;
                        } else if (swaggerJson && "openapi" in swaggerJson) {
                            /**
                             * Swagger 3.x
                             * server url ex: https://localdev-integration.cloud.tibcoapps.com:97/gsbc/{gscbId}/tci/{appId}
                             * Add prefix "/extercom" to the basePath
                             */
                            if (swaggerJson.servers && Array.isArray(swaggerJson.servers) && swaggerJson.servers.length) {
                                const serverUrl = swaggerJson.servers[0].url;
                                // failsafe ? This should never happen. Private endpoint will always have /gsbc/ in path
                                let extercomURL = serverUrl;
                                const POS = serverUrl.indexOf("/gsbc/");
                                if (POS !== -1) {
                                    extercomURL = `${serverUrl.substring(0, POS)}${BASE_CONSTANTS.EXTERCOM}${serverUrl.substring(POS)}`;
                                }
                                endpoint.endpointUrl = extercomURL;
                            }
                        }
                    }
                });
            }
        }
        this.endpoints = endpoints;
        this.setOriginalEndpoints(endpoints);
        this.initializeEndpoints();
        this.findSelectedEndpoint();
        this.endpointsInitialized$.next(true);
    }

    private setOriginalEndpoints(endpoints: ITCIApplicationEndpoint[]) {
        this.originalEndpoints = lodash.cloneDeep(endpoints);
    }

    private revertEndpoints() {
        this.endpoints = lodash.cloneDeep(this.originalEndpoints);
    }

    backToEndpointsList(): void {
        this.isSwaggerUiShown = false;
        this.tools.goToPath(this.location.path(false));
    }

    private copySpecUrl(endpointId: string): void {
        this.showHamburgerMenu[endpointId] = false;
    }

    private downloadSpec(endpoint: ITCIApplicationEndpoint): void {
        const swaggerSpecString: string = JSON.stringify(endpoint.swaggerJson, null, "\t");
        if (swaggerSpecString.length > 0) {
            const fileName: string = this.app.name + (endpoint.name ? "_" + endpoint.name : "") + ".json";
            this.tools.download(fileName, swaggerSpecString, "application/json");
        } else {
            this.toastr.warn("No content in the swagger spec json");
        }
    }

    private getAllAppEndpoints(): void {
        this
            .subscriptions
            .getAllAppEndpoints = this
                .endpointsService
                .getAllEndpoints(
                    this.app.id,
                    this.app.sandboxId
                )
                .finally(() => {
                    this.requestEndpoints = true;
                })
                .subscribe(
                    (endpoints: ITCIApplicationEndpoint[]) => {
                        this.initEndpoints(endpoints);
                    },
                    error => {
                        console.log(error);
                        this.toastr.error("Failed to get endpoints.");
                    }
                );
    }

    private getRowId(endpoint: ITCIApplicationEndpoint): string {
        return `endpoint_row_${endpoint.id}`;
    }

    private initializeEndpoints(): void {
        const location = this.windowRef.nativeWindow.location;

        lodash.forEach(
            this.endpoints,
            (endpoint: ITCIApplicationEndpoint) => {
                this.showHamburgerMenu[endpoint.id] = false;
                if (!lodash.isEmpty(endpoint.swaggerJson)) {
                    endpoint.swaggerUrl = `${location.protocol}//${location.host}${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.APPS}/${this.app.id}/endpoints/${endpoint.id}/swagger`;
                }
            }
        );
    }

    private getUserAuthForEndpoints(): void {
        const thisObj = this;
        this.endpointsService.getUserAuthForEndpoints()
            .subscribe(userAuth => {
                thisObj.isRORoleUser = (userAuth.role === USER_ROLES.RO);
            });
    }

    private checkUserCanUpdateEndpoint() {
        const updateProvider = this.endpointsService.getTenantUpdateProvider(this.app.type);
        if (updateProvider) {
            updateProvider.canUpdateApplication({
                actionType: TCI_UPDATE_ACTION_TYPE.APP_ENDPOINT,
                appId: this.app.id,
                application: this.app,
                tenantType: this.app.type
            }).subscribe(res => this.canUpdateEndpoint = res);
        } else {
            console.warn("No AppUpdate service provider available");
        }
    }

    private monitorURLFragmentChange(): void {
        this
            .subscriptions
            .routerEventsSubscription = this
                .router
                .events
                .subscribe(
                    event => {
                        if (event instanceof NavigationEnd) {
                            const urlFragmentInfo = this.parseUrlFragment(event.urlAfterRedirects);

                            if (
                                urlFragmentInfo.endpointId !== this.selectedEndpointId
                            ) {
                                this.selectedEndpointId = urlFragmentInfo.endpointId;
                                this.findSelectedEndpoint();

                                if (!lodash.isEmpty(this.selectedEndpointId)) {
                                    this.renderSwaggerUI();
                                }
                            }
                        }
                    }
                );
    }

    private findSelectedEndpoint(): void {
        if (this.selectedEndpointId && !lodash.isEmpty(this.endpoints)) {
            if (!this.selectedEndpoint || this.selectedEndpointId !== this.selectedEndpoint.id) {
                this.selectedEndpoint = lodash.find(this.endpoints, {
                    id: this.selectedEndpointId
                });
            }
        }
    }

    notifyCopied(whatsCopied: string): void {
        this.toastr.success(`${whatsCopied} copied to clipboard.`);
    }

    /**
     * Parse a URL and return the values in its fragment. The fragment, in its complete form, is supposed to contain
     * the
     * endpoint id and a flag "view-only" about whether to disable the `Try out` button or not in the Swagger UI. When
     * the endpoint id is present the Swagger UI will be displayed, otherwise the endpoints list will be displayed.
     * When
     * the flag is present the `Try out` button of the Swagger UI will be hidden. These 2 variables will be separated
     * by
     * a comma when they both are present.
     *
     * The URL would look like below
     * https://localdev-integration.cloud.tibco.com/applications/details/endpoints/xjjgi3tjcrrloo4m5fxydxgilbvxl2fb?applicationType=node#avrojpwj62wo6ufuf26yjpzchqkqymi2
     *
     * @param url {string} The URL whose fragment is to be parsed out.
     * @return {
        endpointId: string,
     * } The endpoint id. Their value will be `undefined` if they are not present in the url.
     */
    private parseUrlFragment(url: string): {
        endpointId: string
    } {
        const urlFragment: string = this
            .router
            .parseUrl(url)
            .fragment;
        const splitted: string[] = lodash.isString(urlFragment) ? urlFragment.split(",") : [];

        return {
            endpointId: splitted[0]
        };
    }

    /* Start Mashery related methods */
    private addPackage(): void {

    }

    private addPlan(): void {

    }

    private back(): void {
        this.toastr.clear();
        this.closeAllPopovers();

        if (this.masheryStep === 2 && this.isNewService) {
            this.serviceRepeated = {};
            this.isNewService = false;
        } else if (this.masheryStep === 4) {
            this.masheryPlans = [];
            this.selectedMasheryPlans = [];
        }

        if (this.masheryStep === 2 && this.isLoggedIntoMashery) {
            this.masheryStep = 0;
        } else {
            this.masheryStep -= 1;
        }
    }

    private cancel(): void {
        this.masheryStep = 1;
        this.toastr.clear();
        this.closePublishToMasheryDialog();
    }

    private changeMasheryEndpointsSelection(selected?: boolean): void {
        if (lodash.isBoolean(selected)) {
            if (selected) {
                this.selectedMasheryEndpoints = this.masheryEndpoints.concat();
            } else {
                this.selectedMasheryEndpoints = [];
            }
        }
        this.convertEndpoints();
    }

    private clearMasheryDataProperties(): void {
        this.clearMasheryServicesFilter();
        this.initializeMasheryPublish();
        this.isLoggedIntoMashery = false;
        this.isLoadingMasheryDialogContent = false;
        this.isSavingPopover = false;
        this.selectedMasheryPlans = [];
        this.selectedMasheryEndpoints = [];
        this.selectedMasheryPackage = null;
        this.selectedDomains.domain = null;
        this.domains = [];
        this.domainsToSelect = [];
        this.masheryAction = "";
        this.masheryServices = [];
        this.masheryPackages = [];
        this.masheryPackagesToSelect = [];
        this.masheryStep = -1;
        this.masheryEndpoints = [];
        this.masheryPlans = [];
        this.masheryAction = "";
        this.masheryControlCenter = "";
        this.reformattedMasheryServices = [];
        this.orderedMasheryEndpoints = [];
        this.packageUrl = "";
        this.addToPlanUrl = "";
        this.apiDefinitionUrl = "";
    }

    private closeAllPopovers(): void {
        this.closeNewApiDefinitionDialog();
        this.closeNewPackageDialog();
        this.closeNewPlanDialog();
    }

    private clearMasheryServicesFilter(): void {
        this.servicesFilterKeyword = "";
    }

    private closeNewApiDefinitionDialog(): void {
        if (this.subscriptions.saveMasheryService) {
            this
                .subscriptions
                .saveMasheryService
                .unsubscribe();
            delete this.subscriptions.saveMasheryService;
        }
        this.createApiDefinitionDialogOpen = false;
        this.isSavingPopover = false;
        this.newServiceName = "";
    }

    private closeNewPackageDialog(): void {
        if (this.subscriptions.saveMasheryPackage) {
            this
                .subscriptions
                .saveMasheryPackage
                .unsubscribe();
            delete this.subscriptions.saveMasheryPackage;
        }
        this.createPackageDialogOpen = false;
        this.isSavingPopover = false;
        this.newPackageName = "";
    }

    private closeNewPlanDialog(): void {
        if (this.subscriptions.saveMasheryPlan) {
            this
                .subscriptions
                .saveMasheryPlan
                .unsubscribe();
            delete this.subscriptions.saveMasheryPlan;
        }
        this.createPlanDialogOpen = false;
        this.isSavingPopover = false;
        this.allowSelfProvKeys = false;
        this.newPlanName = "";
    }

    private closePublishToMasheryDialog(): void {
        // TODO: Set all dialog properties to initial values
        this.closeAllPopovers();
        this.clearMasheryDataProperties();
        this.publishToMasheryDialogOpen = false;
    }

    private connectToMashery() {
        if (this.isLoggedIntoMashery) {
            this.masheryStep += 1;
            this.isLoadingMasheryDialogContent = false;
        } else {
            if (this.publishToMasheryForm.valid) {
                this
                    .subscriptions
                    .connectoToMashery = this
                        .endpointsService
                        .loginToMashery(this.app, this.masheryPublish.auth)
                        .subscribe(
                            () => {
                                this
                                    .subscriptions
                                    .connectToMasheryGetServices = this
                                        .getServices()
                                        .subscribe(
                                            this.handleServicesSuccess.bind(this),
                                            this.handleServicesError.bind(this)
                                        );
                            },
                            (error) => {
                                console.log(error);
                                this.handleMasheryError(error);
                                this.isLoadingMasheryDialogContent = false;
                            }
                        );
            }
        }
    }

    private convertDateStringInServices(services: MasheryService[]): MasheryService[] {
        return lodash.map(services, (service: MasheryService) => {
            return lodash.assign({}, service, {
                updated: this.datePipe.transform(new Date(service.updated), "dd MMM y"),
                created: this.datePipe.transform(new Date(service.created), "dd MMM y")
            });
        });
    }

    private convertEndpoints(): void {
        let lastPath = "";
        let lastIndex = 0;

        this.masheryPublish.endpoints = [];
        lodash.forEach(this.selectedMasheryEndpoints, (endpoint: MasheryEndpoint) => {
            if (lastPath !== endpoint.url) {
                lastPath = endpoint.url;
                const ep: MasheryEndpointToPublish = {
                    url: endpoint.url,
                    methods: [],
                    basePath: this.replacePrivateEndpointBasePath(endpoint.basePath)
                };
                if (endpoint.type) {
                    ep.type = endpoint.type;
                }
                lastIndex = this.masheryPublish.endpoints.push(ep);
                lastIndex -= 1;
            }
            this.masheryPublish.endpoints[lastIndex].methods.push(endpoint.method);
        });
    }

    private replacePrivateEndpointBasePath(path: string): string {
        if (this.app.endpointVisibility === "private" && path) {
            path = path.replace(/\/gsbc\/.*\/tci\//g, "/");
        }
        return path;
    }

    private doSorting(): void {

    }

    private fetchDomains(): void {
        this
            .subscriptions
            .fetchDomains = this
                .endpointsService
                .getMasheryDomains(this.app)
                .subscribe(
                    result => {
                        this.domains = result;
                        this.domainsToSelect = this.mapDomainsToSelectItems();
                    },
                    error => {
                        console.log(error);
                        this.handleMasheryError(error);
                    }
                );
    }

    private fetchPackages(moveForward: boolean = false): void {
        this
            .subscriptions
            .fetchPackages = this
                .endpointsService
                .getMasheryPackages(this.app)
                .subscribe(
                    (packages: MasheryPackage[]) => {
                        this.masheryPackages = packages;
                        this.masheryPackagesToSelect = this.mapPackagesToSelectItems();
                        this.isLoadingMasheryDialogContent = false;
                        if (moveForward) {
                            this.masheryStep += 1;
                        }
                    },
                    error => {
                        console.log(error);
                        const errorObj = error.json() || {};
                        const errorMsg = (errorObj.masheryError) ?
                            ((errorObj.masheryError.error) ? errorObj.masheryError.error :
                                this.translate.instant("translate")("NG_APP_ENDPOINTS.PUBLISH_TO_MASHERY.ERR_LOGIN")) :
                            errorObj.message;
                        this.toastr.error(errorMsg);
                        this.isLoadingMasheryDialogContent = false;
                    }
                );
    }

    private filterMasheryServices(): void {
        this.reformattedMasheryServices = this.convertDateStringInServices(
            this.servicesFilterKeyword ?
                lodash.filter(this.masheryServices, (service: MasheryService) => {
                    return lodash.includes(service[this.filterBy], this.servicesFilterKeyword);
                }) :
                this.masheryServices
        );
    }

    private generateMasheryUrls(): void {
        // For the package: https://<Mashery Control Center URL>/control-center/api-packages/<Package UUID>
        // For the plan: https://<Mashery Control Center URL>/control-center/api-packages/<Package UUID>/plans/<Plan
        // UUID> For the API definition: https://<Mashery Control Center URL>/control-center/api-definitions/<API ID>
        // For the developer portal: We can't use a link yet, there is no pattern that we can follow there For the 'add
        // them to a plan': https://<Mashery Control Center URL>/control-center/api-packages/<Package UUID>/plans/new

        const masheryUrl: string = this.masheryControlCenter;

        this.apiDefinitionUrl = masheryUrl + "/control-center/api-definitions/" + this.masheryPublish.service.id;
        /**
         * https://jira.tibco.com/browse/IPAS-9577
         * Added "masheryControlCenterUrl" and replaced apiDefinitionUrl - Li Huan [ huali ]
         * Point "packageUrl" and "selectedMasheryPlans" url to "masheryControlCenterUrl" - Rajesh Kumar [ rajekuma ]
         */

         /**
          * TSC user with mashery subscription : Send to mashery control center URL (IPAS-9577)
          * Legacy mashery user : Send to traffic manager admin domain URL (IPAS-15887)
          */
        this.packageUrl = this.isLoggedIntoMashery ? this.masheryControlCenterUrl :
            `${masheryUrl}/control-center/api-packages/${this.selectedMasheryPackage.id}`;
        this.addToPlanUrl = masheryUrl + "/control-center/api-packages/" + this.selectedMasheryPackage.id + "/plans/new";
        lodash.forEach(this.selectedMasheryPlans, (value, key) => {

        this.selectedMasheryPlans[key].url = this.isLoggedIntoMashery ? this.masheryControlCenterUrl :
            `${masheryUrl}/control-center/api-packages/${this.selectedMasheryPackage.id}/plans/${value.id}`;
        });
    }

    private getServices(): Observable<MasheryService[]> {
        const params: GetServicesParameters = {
            "sort": "name:asc",
            "offset": 0,
            "limit": 1000
        };

        return this
            .endpointsService
            .getMasheryServices(this.app, params);
    }

    private handlePublishEndpointsSuccess(): void {
        // TODO: To be completed
        this.generateMasheryUrls();
        this.toastr.success("Successfully published endpoints to Mashery");
        this.isLoadingMasheryDialogContent = false;
        this.masheryStep += 1;
    }

    private handleServicesSuccess(services: MasheryService[]): void {
        this.currentPage = 1;
        this.masheryServices = services;
        this.reformattedMasheryServices = this.convertDateStringInServices(this.masheryServices);
        this.masheryControlCenter = "https://" +
            this.masheryPublish.auth.trafficManagerDomain.replace(".api.", ".admin.");
        this.masheryStep += 1;
        this.isLoadingMasheryDialogContent = false;
    }

    private handleServicesError(error): void {
        const errorMsg = (error.masheryError) ?
            ((error.masheryError.error) ?
                error.masheryError.error :
                error.status) :
            error.message;
        this.toastr.error(errorMsg);
        this.isLoadingMasheryDialogContent = false;
    }

    private handleMasheryError(error): void {
        const errorObj = error.json();
        let errJson = {};
        let errMsg = "";
        let errMsgJson;
        const arrErrorMsg = [];
        let errorMsg = "";

        if (errorObj.masheryError) {
            if (errorObj.masheryError.error) {
                errorMsg = errorObj.masheryError.error;
            } else {
                try {
                    errJson = JSON.parse(errorObj.status);
                    lodash.forEach(errJson, (errJsonItem: any) => {
                        errMsg = errJsonItem.message;
                        errMsgJson = JSON.parse(errMsg.substr(errMsg.indexOf("{")));
                        errorMsg = errMsgJson.errorMessage;
                        if (errMsgJson.errors.length > 0) {
                            lodash.forEach(errMsgJson.errors, err => {
                                // Note: ignore unreadable error message that ends with **Ref
                                if (err.property && err.message && !err.property.endsWith("Ref") && !arrErrorMsg.includes(err.message)) {
                                    arrErrorMsg.push(err.message.replace(/<[^>]*>/g, ""));
                                }
                            });
                        }
                    });
                } catch (e) {
                    errorMsg = errorObj.status;
                }
            }
        } else {
            errorMsg = errorObj.message;
            if (errorObj.status) {
                try {
                    errJson = JSON.parse(errorObj.status);
                    lodash.forEach(errJson, (errJsonItem: any) => {
                        errMsg = errJsonItem.errorMessage;
                        errMsgJson = JSON.parse(errMsg);
                        errorMsg = errMsgJson.errorMessage;

                        if (errMsgJson.errors.length > 0) {
                            lodash.forEach(errMsgJson.errors, err => {
                                // Note: ignore unreadable error message that ends with **Ref
                                if (err.property && err.message && !err.property.endsWith("Ref") && !arrErrorMsg.includes(err.message)) {
                                    arrErrorMsg.push(err.message.replace(/<[^>]*>/g, ""));
                                }
                            });
                        }
                    });
                } catch (e) {
                    errorMsg = errorObj.status;
                }
            }
        }
        if (arrErrorMsg.length) {
            errorMsg = arrErrorMsg.join("<br/>");
        } else {
            // Note: replace html tag in errorMsg
            errorMsg = errorMsg.replace(/<[^>]*>/g, "");
        }
        this.toastr.error("", errorMsg);
    }

    private initPublishToMasheryDialog(): Observable<any> {
        return this
            .endpointsService
            .checkMasherySubscription(this.app)
            .map(result => {
                this.fetchDomains();
                this.isLoggedIntoMashery = result;
                this.masheryStep = result ? 0 : 1;
                return result;
            })
            .catch(error => {
                console.log(error);
                this.isLoggedIntoMashery = false;
                this.masheryStep = 1;
                return Observable.of(null);
            });
    }

    private initializeMasheryPublish(): void {
        this.masheryPublish = {
            auth: {
                username: "",
                password: "",
                areaUuid: "",
                trafficManagerDomain: ""
            },
            service: null,
            endpoints: []
        };
    }

    private keys(obj: Object): string[] {
        return Object.keys(obj);
    }

    private length(arrObj: Array<any> | Object): number {
        return lodash.size(arrObj);
    }

    private loadServices(clear) {
        const params: GetServicesParameters = {
            "sort": this.sort || "name:asc",
            "offset": this.currentPage * this.itemsPerPage,
            "limit": this.itemsPerPage
        };

        this.loadingServices = true;

        if (this.servicesFilterKeyword) {
            params.filter = this.filterAttribute + ":" + this.servicesFilterKeyword;
        }

        clear = clear || false;

        this
            .subscriptions
            .loadServices = this
                .endpointsService
                .getMasheryServices(this.app, params)
                .subscribe(
                    (services: MasheryService[]) => {
                        this.loadingServices = false;

                        if (clear) {
                            this.masheryServices = [];
                        }

                        this.masheryServices = this.masheryServices.concat(services);
                        this.reformattedMasheryServices = this.convertDateStringInServices(this.masheryServices);
                    },
                    error => {
                        console.log(error);
                        this.handleMasheryError(error);

                    }
                );
    }

    private mapDomainsToSelectItems(): SelectItem[] {
        return lodash.map(this.domains, (domain: MasheryDomain) => {
            return {
                label: domain.domain,
                value: domain
            };
        });
    }

    private mapPackagesToSelectItems(): SelectItem[] {
        return lodash.map(this.masheryPackages, (masheryPackage: MasheryPackage) => {
            return {
                label: masheryPackage.name,
                value: masheryPackage
            };
        });
    }

    private nextStep(): void {
        this.toastr.clear();
        this.isLoadingMasheryDialogContent = true;

        switch (this.masheryStep) {
            case 0:
                console.log("case 0");
                this.fetchDomains();
                break;
            case 1:
                console.log("case 1");
                this.connectToMashery();
                break;
            case 2:
                console.log("case 2");
                this.selectService();
                break;
            case 3:
                console.log("case 3");
                this.fetchPackages(true);
                break;
            case 4:
                console.log("case 4");
                this.publishEndpoints();
                break;
            default:
                break;
        }
    }

    private nextToLogin(): void {
        this
            .subscriptions
            .nextToLoginGetServices = this
                .getServices()
                .subscribe(
                    (services: MasheryService[]) => {
                        this.handleServicesSuccess(services);
                        this.nextStep();
                    },
                    this.handleServicesError.bind(this)
                );
    }

    private openCreateAPIDefinitionDialog(): void {
        this.createApiDefinitionDialogOpen = true;
    }

    private openCreatePackageDialog(): void {
        this.createPackageDialogOpen = true;
    }

    private openCreatePlanDialog(): void {
        this.createPlanDialogOpen = true;
    }

    private openPublishToMasheryDialog(endpoint: ITCIApplicationEndpoint): void {
        this.selectedEndpoint = endpoint;
        this.isLoadingMasheryDialogContent = true;
        this.publishToMasheryDialogOpen = true;
        this
            .initPublishToMasheryDialog()
            .subscribe(
                () => {
                    this.isLoadingMasheryDialogContent = false;
                }
            );
    }

    private orderMasheryEndpointsByUrl(): MasheryEndpoint[] {
        return lodash.sortBy(this.masheryEndpoints, [
            "url"
        ]);
    }

    private parseEndpoints() {
        this.masheryEndpoints = [];
        this.selectedMasheryEndpoints = [];

        if (this.selectedEndpoint && this.selectedEndpoint.swaggerJson) {
            const swaggerJson: SwaggerSpec = this.selectedEndpoint.swaggerJson;
            const endpointType: string = this.app.endpointVisibility;

            lodash.forEach(swaggerJson.paths, (path: Path, pathName: string) => {
                lodash.forEach(
                    path,
                    (operation: string | Operation | Array<Parameter | Reference>, method: string) => {
                        if (!lodash.isString(operation) && !lodash.isArray(operation)) {
                            const endpoint: MasheryEndpoint = {
                                url: pathName,
                                method: method,
                                basePath: this.getMasheryEndpointBasePath(swaggerJson)
                            };

                            if (endpointType !== "public") {
                                endpoint.type = "tci";
                            }

                            this.masheryEndpoints.push(endpoint);
                            this.selectedMasheryEndpoints = this.masheryEndpoints.concat();
                            this.convertEndpoints();
                        }
                    }
                );
            });

            this.orderedMasheryEndpoints = this.orderMasheryEndpointsByUrl();
        } else {
            const errorMsg = this.translate.instant("TROPOS_MASHERY.ERR_SWAGGER");
            this.toastr.error(errorMsg);
        }
    }

    /**
     * Extract basePath from swaggerJson
     * @param swaggerJson
     * @returns {string}
     */
    private getMasheryEndpointBasePath(swaggerJson: SwaggerSpec): string {
        let basePath = "";
        if ("swagger" in swaggerJson) { // Swagger 2.x
            basePath = swaggerJson.basePath;
        } else if ("openapi" in swaggerJson) { // Swagger 3.x
            if (swaggerJson.servers && Array.isArray(swaggerJson.servers) && swaggerJson.servers.length) {
                if (this.app.endpointVisibility === ITCIEndpointVisibilityType.private) {
                    /**
                     * Private OAS3 server url example:
                     * https://localdev-integration.cloud.tibcoapps.com:97/gsbc/01F3ZXQAEQEH5J5WF3GZ4X1195/tci/dl2uca5ives26lqsg5dveri67nmdg5hd
                     * basePath = /gsbc/01F3ZXQAEQEH5J5WF3GZ4X1195/tci/dl2uca5ives26lqsg5dveri67nmdg5hd + any subPath after this.
                     */
                    basePath = this.extractPath(/\/gsbc\/.*/, swaggerJson.servers[0].url);
                } else {
                    /**
                     * Public OAS3 server url example:
                     * https://localdev-integration.cloud.tibcoapps.com:97/pbc6q7htlp43gt7r5u57cymxn27pb2kr
                     * basePath = /dl2uca5ives26lqsg5dveri67nmdg5hd + any subPath after this.
                     */
                    basePath = this.extractPath(
                            new RegExp("\\b/" + this.app.id + ".*\\b"),
                            swaggerJson.servers[0].url
                        );
                }
            }
        }
        return basePath;
    }

    private extractPath(pattern: RegExp, url: string): string {
        const pathMatch = url.match(pattern) || [];
        return pathMatch[0] || "";
    }

    private publishEndpoints(): void {
        if (this.masheryPublish.endpoints.length) {
            if (this.masheryAction === "add") {
                this
                    .subscriptions
                    .addEndpointsToPackage = this
                        .endpointsService
                        .createMasheryEndpoints(this.app, this.masheryPublish.service.id, {
                            trafficManagerDomain: this.masheryPublish.auth.trafficManagerDomain,
                            appId: this.app.id,
                            appName: this.app.name,
                            endpoints: this.masheryPublish.endpoints
                        })
                        .subscribe(
                            this.handlePublishEndpointsSuccess.bind(this),
                            error => {
                                this.generateMasheryUrls();
                                console.log(error);
                                this.isLoadingMasheryDialogContent = false;
                                this.handleMasheryError(error);
                            }
                        );
            } else {
                this
                    .subscriptions
                    .publishEndpointsToPackage = this
                        .endpointsService
                        .createMasheryPlanEndpoints(
                            this.app,
                            this.selectedMasheryPackage.id,
                            this.masheryPublish.service.id,
                            lodash.map(this.selectedMasheryPlans, "id"),
                            {
                                trafficManagerDomain: this.masheryPublish.auth.trafficManagerDomain,
                                appId: this.app.id,
                                appName: this.app.name,
                                endpoints: this.masheryPublish.endpoints
                            }
                        )
                        .subscribe(
                            this.handlePublishEndpointsSuccess.bind(this),
                            error => {
                                this.generateMasheryUrls();
                                console.log(error);
                                this.isLoadingMasheryDialogContent = false;
                                this.handleMasheryError(error);
                            }
                        );
            }
        } else {
            this.toastr.warn("No endpoints were selected to be published.");
            this.isLoadingMasheryDialogContent = false;
        }
    }

    private publishToMashery(action: string): void {
        this.masheryAction = action;
        this.nextStep();
    }

    private saveMasheryService(): void {
        this.isSavingPopover = true;

        const version = lodash.get(this.app, "appVersion");

        this
            .subscriptions
            .saveMasheryService = this
                .endpointsService
                .createMasheryService(this.app, this.newServiceName, version)
                .subscribe(
                    res => {
                        this
                            .toastr
                            .success("New API definition saved.");
                        this.closeNewApiDefinitionDialog();
                        this.isLoadingMasheryDialogContent = true;
                        this
                            .subscriptions
                            .saveMasheryServiceGetServices = this
                                .getServices()
                                .subscribe(
                                    (services: MasheryService[]) => {
                                        this.masheryServices = services;
                                        this.reformattedMasheryServices = this.convertDateStringInServices(services);
                                        this.isLoadingMasheryDialogContent = false;
                                    },
                                    error => {
                                        console.log(error);
                                        this.handleMasheryError(error);
                                    }
                                );
                    },
                    error => {
                        console.log(error);
                        this.isSavingPopover = false;
                        this.handleMasheryError(error);
                    }
                );
    }

    private saveMasheryPackage(): void {
        this.isSavingPopover = true;

        this
            .subscriptions
            .saveMasheryPackage = this
                .endpointsService
                .createMasheryPackage(this.app, this.newPackageName)
                .subscribe(
                    res => {
                        this
                            .toastr
                            .success("New package saved.");
                        this.fetchPackages();
                        this.closeNewPackageDialog();
                    },
                    error => {
                        console.log(error);
                        this.isSavingPopover = false;
                        this.handleMasheryError(error);
                    }
                );
    }

    private saveMasheryPlan(): void {
        this.isSavingPopover = true;

        this
            .subscriptions
            .saveMasheryPlan = this
                .endpointsService
                .createMasheryPlan(this.app, this.selectedMasheryPackage.id, this.newPlanName, this.allowSelfProvKeys)
                .subscribe(
                    res => {
                        this
                            .toastr
                            .success("New plan saved.");
                        this.selectPackage({
                            value: this.selectedMasheryPackage
                        });
                        this.closeNewPlanDialog();
                    },
                    error => {
                        console.log(error);
                        this.isSavingPopover = false;
                        this.handleMasheryError(error);
                    }
                );
    }

    private selectPackage(event): void {
        const selectedPackage = event.value as MasheryPackage;
        // this.selectedMasheryPackage = selectedPackage;
        this.selectedMasheryPlans = [];

        if (selectedPackage) {
            this
                .subscriptions
                .selectPackageGetMasheryPlans = this
                    .endpointsService
                    .getMasheryPlans(this.app, selectedPackage.id)
                    .subscribe(
                        (plans: MasheryPlan[]) => {
                            this.masheryPlans = plans;
                        },
                        error => {
                            console.log(error);
                            this.handleMasheryError(error);
                        }
                    );
        }
    }

    private selectDomain(event): void {
        const newSelectedDomain: MasheryDomain = event.value;

        this.masheryPublish.auth.areaUuid = newSelectedDomain.id;
        this.masheryPublish.auth.trafficManagerDomain = newSelectedDomain.domain;
    }

    private selectService(): void {
        let errorMsg = "";

        if (this.validateService()) {
            if (this.endpoints.length > 0) {

                if (this.isNewService) {
                    const version = "version" in this.app ? this.app["version"] : "1.0";
                    this
                        .subscriptions
                        .selectServiceCreateMasheryService = this
                            .endpointsService
                            .createMasheryService(this.app, this.masheryPublish.service.name, version)
                            .subscribe(
                                (service: MasheryService) => {
                                    if (service) {
                                        this.loadServices(true);
                                        this.masheryPublish.service = service;
                                        this.isNewService = false;
                                        this.masheryStep += 1;
                                        this.isLoadingMasheryDialogContent = false;
                                    }
                                },
                                (error) => {
                                    console.log(error);
                                    this.handleMasheryError(error);
                                    this.isLoadingMasheryDialogContent = false;
                                }
                            );
                } else {
                    this.masheryStep += 1;
                    this.isLoadingMasheryDialogContent = false;
                }

                this.parseEndpoints();
            } else {
                errorMsg = this.translate.instant("NG_APP_ENDPOINTS.PUBLISH_TO_MASHERY.ERR_ENDPOINTS");
                this.isLoadingMasheryDialogContent = false;
            }
        }

        if (errorMsg) {
            this.toastr.error(errorMsg);
        }
    }

    private setFilter(key: string): void {

    }

    private switchToClassicFlow(): void {
        this.isLoggedIntoMashery = false;
        this.masheryPublish.auth.areaUuid = "";
        this.masheryPublish.auth.trafficManagerDomain = "";
        this.masheryStep = 1;
    }

    private updateService(service: MasheryService): void {
        this.masheryPublish.service = service;
        this.isNewService = false;
        this.nextStep();
    }

    private validateForm(): void {

    }

    private validateService(): boolean {
        this.serviceRepeated = {};
        if (this.isNewService && this.publishToMasheryForm.valid) {
            const search = lodash.filter(this.masheryServices, (service: MasheryService) => {
                return this.masheryPublish.service && service.id === this.masheryPublish.service.id;
            });

            if (search.length > 0) {
                this.serviceRepeated = search[0];
                return false;
            } else {
                return true;
            }
        } else {
            return !!(!this.isNewService && this.masheryPublish.service.id);
        }
    }

    /* End Mashery related methods */

    protected getSwaggerSpec(): SwaggerSpec {
        return lodash.find(this.endpoints, {
            id: this.selectedEndpointId
        }).swaggerJson;
    }

    /**
     * For pirvate endpoint only
     * IPAS-17798: WebServer to update Swagger basePath and host for API tester.
     * https://jira.tibco.com/browse/IPAS-17798
     *
     * Update swagger v2 host and basePath or v3 sever URL on fly without impacting the original uploaded swagger.
     * host -> public TCI domain
     * /gsbc/{gsbc}/tci/{appId} --> /proxy/apps/{appId}/mesh
     */
    updateSwaggerSpec(spec: SwaggerSpec): SwaggerSpec {
        if ("swagger" in spec) { // Swagger 2.x
            spec.host = this.TCIDomainName;
            const subPath = this.getPrivateEndpointSubPath(spec.basePath);
            spec.basePath = this.getPrivateSwaggerBasePath(subPath);
        } else if ("openapi" in spec) { // Swagger 3.x
            spec.servers.forEach((server) => {
                const subPath = this.getPrivateEndpointSubPath(server.url);
                server.url = `https://${this.TCIDomainName}${this.getPrivateSwaggerBasePath(subPath)}`;
            });
        } else {
            console.error("invalid swagger spec : ", spec);
        }
        return spec;
    }

    getPrivateSwaggerBasePath(subPath: string): string {
        return `${lodash.replace(BASE_CONSTANTS.PRIVATE_ENDPOINT_SWAGGER_BASE_PATH, /{appId}/, this.app.id)}${subPath ? subPath : ""}`;
    }

    getPrivateEndpointSubPath(path: string): string {
        /** For pirvate endpoint only
         * Extracting subPath from basePath -
         * Swagger 2.x basePath pattern without subPath: /gsbc/{gsbc}/tci/{appId}
         * Swagger 2.x basePath pattern with subPath: /gsbc/{gsbc}/tci/{appId}/subPath
         *
         * Swagger 3.x server url pattern without subPath: https://localdev-integration.cloud.tibcoapps.com:97/gsbc/{gsbc}/tci/{appId}
         * Swagger 3.x server url pattern with subPath: https://localdev-integration.cloud.tibcoapps.com:97/gsbc/{gsbc}/tci/{appId}/subPath
         */
        return path.split(`/tci/${this.app.id}`).pop() || "";
    }

    renderSwaggerUI(): void {
        const swaggerSpec: SwaggerSpec = (this.app.endpointVisibility === ITCIEndpointVisibilityType.private) ?
        this.updateSwaggerSpec(lodash.cloneDeep(this.getSwaggerSpec())) : this.getSwaggerSpec();
        // Note: only show "Try it out" button when app status is Running.
        const isShowTryItOut = this.endpointsService.getAppStatus(this.app).status.getLabel() === "Running";
        const disableTryItOutPlugin = function() {
           return {
               statePlugins: {
                   spec: {
                       wrapSelectors: {
                           allowTryItOutFor: () => () => isShowTryItOut,
                       }
                   }
               }
           };
        };
        this.isSwaggerUiShown = true;
        try {
            SwaggerUIBundle({
                spec: swaggerSpec,
                dom_id: "#swagger_ui_container",
                defaultModelsExpandDepth: -1,
                docExpansion: "list",
                displayOperationId: true,
                displayRequestDuration: true,
                supportedSubmitMethods: [
                    "get",
                    "post",
                    "put",
                    "delete",
                    "patch"
                ],
                presets: [
                    SwaggerUIBundle.presets.apis,
                    SwaggerUIStandalonePreset
                ],
                plugins: [
                    disableTryItOutPlugin
                ],
                layout: "BaseLayout"
            });
        } catch (error) {
            console.log(error);
            this.toastr.error(error);
        } finally {
            console.log("Swagger UI displayed.");
        }
    }

    /**
     * A sample URL would look like below
     * /applications/details/endpoints/xjjgi3tjcrrloo4m5fxydxgilbvxl2fb?applicationType=node#avrojpwj62wo6ufuf26yjpzchqkqymi2
     *
     * @param endpoint
     */
    showSwaggerUI(endpoint: ITCIApplicationEndpoint): void {
        this.tools.goToPath(`${this.location.path(false)}#${endpoint.id}`);
        if (this.app.endpointVisibility === ITCIEndpointVisibilityType.private) {
            const ep = lodash.cloneDeep(endpoint);
            const subPath = this.getPrivateEndpointSubPath(`/tci/${ep.endpointName}`);
            ep.endpointUrl = `https://${this.TCIDomainName}${this.getPrivateSwaggerBasePath(subPath)}`;
            this.selectedEndpoint = ep;
            return;
        }
        this.selectedEndpoint = endpoint;
    }
}
